import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
/** The handoff persists under this key. Kept identical so a prototype session carries over. */
export const THEME_STORAGE_KEY = 'lb-theme';
/**
 * The anti-flash script.
 *
 * Inlined into `<head>` ahead of any stylesheet so `data-theme` is on `<html>`
 * before the first paint. Without it a dark-mode member sees a cream flash on
 * every navigation — and on this app that matters more than on the back office,
 * because the public routes are prerendered: the HTML arrives already painted,
 * so there is a real frame to flash in.
 *
 * Deliberately dependency-free and synchronous. It cannot import the constant
 * above, because it is stringified into the document rather than bundled — the
 * key appears twice and the test in tests/theme.test.ts asserts they agree.
 */
export const THEME_INIT_SCRIPT = `
try {
  var t = localStorage.getItem('lb-theme');
  if (t !== 'light' && t !== 'dark') {
    t = matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  document.documentElement.setAttribute('data-theme', t);
  document.documentElement.style.colorScheme = t;
} catch (_) {
  document.documentElement.setAttribute('data-theme', 'light');
}
`.trim();
const ThemeContext = createContext(null);
function read() {
    if (typeof document === 'undefined')
        return 'light';
    // The init script already resolved this, including the system preference.
    // Reading the attribute rather than storage keeps one answer, not two.
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}
export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(read);
    const setTheme = useCallback((next) => {
        setThemeState(next);
        document.documentElement.setAttribute('data-theme', next);
        document.documentElement.style.colorScheme = next;
        try {
            // eslint-disable-next-line no-restricted-globals -- the documented owner of this key (§6.5)
            localStorage.setItem(THEME_STORAGE_KEY, next);
        }
        catch {
            // Private mode. The choice still applies for this session.
        }
    }, []);
    /*
     * Follow the system only while the member has not chosen. Once they have, a
     * laptop switching to dark at sunset must not override them.
     */
    useEffect(() => {
        let stored = null;
        try {
            // eslint-disable-next-line no-restricted-globals -- see above
            stored = localStorage.getItem(THEME_STORAGE_KEY);
        }
        catch {
            return;
        }
        if (stored === 'light' || stored === 'dark')
            return;
        const query = matchMedia('(prefers-color-scheme: dark)');
        const onChange = (event) => {
            const next = event.matches ? 'dark' : 'light';
            setThemeState(next);
            document.documentElement.setAttribute('data-theme', next);
            document.documentElement.style.colorScheme = next;
        };
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);
    const value = useMemo(() => ({ theme, setTheme, toggle: () => setTheme(theme === 'dark' ? 'light' : 'dark') }), [theme, setTheme]);
    return _jsx(ThemeContext, { value: value, children: children });
}
export function useTheme() {
    const value = use(ThemeContext);
    if (!value)
        throw new Error('useTheme must be used inside <ThemeProvider>');
    return value;
}
//# sourceMappingURL=theme.js.map